<template>
    <v-data-table class="elevation-0"
                  :headers="headers"
                  :items="tableItems"
                  :items-per-page="15"
                  :footer-props="dataTableFooterProps"
                  :search="dataTableSearch"
                  :custom-filter="dataTableCustomFilter"
                  :loading="isBusy"
                  :loading-text="$t('common.loading_table_data')"
    >
        <template v-slot:item.brand_count="{ item }">
            {{ item.brand_count }}
        </template>
        <template v-slot:item.product_type_count="{ item }">
            {{ item.product_type_count }}
        </template>
        <template v-slot:top>
            <v-toolbar flat color="white">
                <v-text-field
                    v-model="dataTableSearch"
                    prepend-inner-icon="mdi-magnify"
                    :placeholder="$t('common.filter')"
                    outlined
                    dense
                    hide-details
                />
                <v-spacer/>
                <p class="mr-3 mt-2">
                    <v-icon>
                        {{ synchronizationIcon(synchronization) }}
                    </v-icon>
                    {{ synchronization ? $t('synchronization.status.' + synchronization.status) : '-' }}
                    ({{ synchronization ? $moment(synchronization.date_time).format('LLL') : '-' }})
                    <span v-if="synchronization && synchronization.status === 'error' && synchronization.error" class="error--text">
                        - {{ synchronization.error }}
                    </span>
                </p>
                <v-btn
                    v-if="!synchronization || (synchronization.status !== 'waiting' && synchronization.status !== 'synchronizing')"
                    color="success"
                    dark
                    class="mb-2"
                    @click="onSynchronize"
                    :loading="isBusySynchronize"
                >
                    {{ $t('button.synchronize') }}
                </v-btn>
            </v-toolbar>
        </template>
        <template v-slot:item.address="{ item }">
            {{ item.address }}
        </template>
        <template v-slot:item.carrier_service_name="{ item }">
            <template v-if="item.carrier_service_name">
                {{ item.carrier_service_name }}
            </template>
            <template v-else>
                <span class="text-no-wrap">
                    <v-icon color="warning">mdi-alert</v-icon>
                    {{ $t('warehouses.no_carrier_service') }}
                </span>
            </template>
        </template>
        <template v-slot:item.buyer_type="{ item }">
            {{ item.buyer_type }}
        </template>
        <template v-slot:item.actions="{ item }">
            <span class="text-no-wrap">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on }">
                        <v-icon small class="mr-2" @click="onUpdateWarehouse(item)" v-on="on">
                            mdi-pencil
                        </v-icon>
                    </template>
                    <span>{{ $t('tooltip.warehouse.update') }}</span>
                </v-tooltip>
            </span>
        </template>
        <template v-slot:item.send_order_created_emails="{ item }">
            {{ item.send_order_created_emails ? $t('common.yes') : $t('common.no') }}
        </template>
        <template v-slot:item.send_order_waiting_for_approval_emails="{ item }">
            {{ item.send_order_waiting_for_approval_emails ? $t('common.yes') : $t('common.no') }}
        </template>
    </v-data-table>
</template>

<script>
import {API} from "@/api/api";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import SynchronizationMixin from "@/mixins/SynchronizationMixin";
import DataTableMixin from "@/mixins/DataTableMixin";

export default {
    name: 'WarehousesTableComponent',
    mixins: [SnackbarMixin, SynchronizationMixin, DataTableMixin],
    data: () => ({
        // loading
        isBusyWarehouses: false,
        isBusyBuyerTypes: false,
        isBusySynchronize: false,
        isBusySynchronization: false,
        // table data
        items: [],
        // buyer types
        buyerTypes: [],
        // synchronization data
        synchronization: null
    }),
    computed: {
        isBusy() {
            return this.isBusyWarehouses || this.isBusyBuyerTypes || this.isBusySynchronize || this.isBusySynchronization;
        },
        headers() {
            return [
                {
                    text: this.$t('warehouses.name'),
                    value: 'name'
                },
                {
                    text: this.$t('warehouses.address'),
                    value: 'address'
                },
                {
                    text: this.$t('warehouses.ico'),
                    value: 'ico'
                },
                {
                    text: this.$t('warehouses.dic'),
                    value: 'dic'
                },
                {
                    text: this.$t('warehouses.carrier_service'),
                    value: 'carrier_service_name'
                },
                {
                    text: this.$t('warehouses.buyer_type'),
                    value: 'buyer_type'
                },
                {
                    text: this.$t('warehouses.brands_count'),
                    value: 'brand_count'
                },
                {
                    text: this.$t('warehouses.product_types_count'),
                    value: 'product_type_count'
                },
                {
                    text: this.$t('warehouses.send_order_created_emails'),
                    value: 'send_order_created_emails'
                },
                {
                    text: this.$t('warehouses.send_order_waiting_for_approval_emails'),
                    value: 'send_order_waiting_for_approval_emails'
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'end',
                    sortable: false
                }
            ];
        },
        tableItems() {
            return this.items.map(warehouse => this.withDataTableSearch({
                ...warehouse,
                address: this.address(warehouse),
                carrier_service_name: warehouse.carrier_service ? warehouse.carrier_service.name : "",
                buyer_type: this.buyerType(warehouse),
                brand_count: warehouse.brand_ids.length,
                product_type_count: warehouse.product_type_ids.length
            }, [
                warehouse.name,
                this.address(warehouse),
                warehouse.ico,
                warehouse.dic,
                warehouse.carrier_service ? warehouse.carrier_service.name : "",
                this.buyerType(warehouse),
                warehouse.brand_ids.length,
                warehouse.product_type_ids.length,
                warehouse.send_order_created_emails ? this.$t('common.yes') : this.$t('common.no'),
                warehouse.send_order_waiting_for_approval_emails ? this.$t('common.yes') : this.$t('common.no')
            ]));
        }
    },
    methods: {
        fetchWarehouses() {
            this.isBusyWarehouses = true;
            API.get('/api/warehouses')
                .then(response => {
                    this.items = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyWarehouses = false;
                });
        },
        fetchBuyerTypes() {
            this.isBusyBuyerTypes = true;
            API.get('/api/buyer-types')
                .then(response => {
                    this.buyerTypes = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyBuyerTypes = false;
                });
        },
        fetchSynchronization() {
            this.isBusySynchronization = true;
            API.get('/api/synchronizations/warehouse')
                .then(response => {
                    this.synchronization = response.data;
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySynchronization = false;
                });
        },
        onSynchronize() {
            this.isBusySynchronize = true;
            API.post('/api/warehouses/synchronize', [])
                .then(() => {
                    this.snackbarSuccess(this.$t('snackbar.warehouses.synchronized'));
                    this.fetchWarehouses();
                    this.fetchSynchronization();
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusySynchronize = false;
                });
        },
        address(warehouse) {
            return [
                warehouse.company,
                warehouse.street,
                warehouse.city,
                warehouse.postalCode,
                warehouse.country,
            ].filter(x => x).join(', ');
        },
        buyerType(warehouse) {
            const buyerType = this.buyerTypes.find(buyerType => buyerType.external_id === warehouse.buyer_type_external_id);
            return buyerType ? buyerType.name : '';
        },
        onUpdateWarehouse(warehouse) {
            this.$router.push({name: 'warehouse', params: {warehouseId: warehouse.id}})
        }
    },
    created() {
        this.fetchBuyerTypes();
        this.fetchWarehouses();
        this.fetchSynchronization();
    }
}
</script>
